
import { useEffect, useState } from 'react';
import { useKeycloakContext } from '@/contexts/KeycloakContext';
import axios from 'axios';

export type UserRole = 'site_user' | 'admin' | 'moderator';

export const useUserRole = () => {
  const { user, isAuthenticated, isLoading: authLoading } = useKeycloakContext();
  const [role, setRole] = useState<UserRole | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const determineUserRole = async () => {
      if (!isAuthenticated || !user) {
        console.log('User not authenticated, setting role to null');
        setRole(null);
        setIsLoading(false);
        return;
      }

      try {
        console.log('Determining role for user:', user.sub);
        
        // Check if we have roles directly from Keycloak first as it's more reliable
        if (user.roles && Array.isArray(user.roles) && user.roles.length > 0) {
          console.log('User roles from Keycloak:', user.roles);
          
          // Case insensitive check for roles
          const normalizedRoles = user.roles.map(r => 
            typeof r === 'string' ? r.toLowerCase() : ''
          );
          
          if (normalizedRoles.includes('admin')) {
            console.log('Setting user role to admin from Keycloak');
            setRole('admin');
            setIsLoading(false);
            return;
          } else if (normalizedRoles.includes('moderator')) {
            console.log('Setting user role to moderator from Keycloak');
            setRole('moderator');
            setIsLoading(false);
            return;
          }
          
          console.log('No admin or moderator role found in Keycloak roles');
        } else {
          console.log('No roles found in Keycloak user object');
        }
        
        // If no roles from Keycloak, try database
        try {
          console.log(`Fetching role from database for user ID: ${user.sub}`);
          const response = await axios.get(`/api/users/${user.sub}/role`, {
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            },
            timeout: 5000
          });
          
          console.log('Database role response:', response.data);
          
          if (response.data) {
            // Try to extract role from various response formats
            let dbRole = null;
            
            if (typeof response.data === 'string') {
              dbRole = response.data;
            } else if (typeof response.data === 'object') {
              if (response.data.role && typeof response.data.role === 'string') {
                dbRole = response.data.role;
              } else if (response.data.roles && Array.isArray(response.data.roles) && response.data.roles.length > 0) {
                dbRole = response.data.roles[0];
              }
            }
            
            if (dbRole) {
              const normalizedRole = dbRole.toLowerCase();
              if (['admin', 'moderator', 'site_user'].includes(normalizedRole)) {
                console.log('Setting user role from database:', normalizedRole);
                setRole(normalizedRole as UserRole);
                setIsLoading(false);
                return;
              }
            }
          }
          
          console.log('No valid role found in database response, falling back to default');
        } catch (dbError) {
          console.error('Error fetching role from database:', dbError);
          // Continue with default role
        }
        
        // Set default role for authenticated users if no specific role was found
        console.log('Setting user role to site_user as default');
        setRole('site_user');
        
      } catch (error) {
        console.error('Error determining user role:', error);
        // Safer fallback to site_user for production
        setRole('site_user');
      } finally {
        setIsLoading(false);
      }
    };

    if (!authLoading) {
      determineUserRole();
    }
  }, [user, isAuthenticated, authLoading]);

  return { role, isLoading };
};
